import { Copy, Trash2, Mail, SendHorizonal } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { env } from "@/lib/env";
import { ROLE_LABEL } from "./role-meta";

export function TeamInvitesList() {
  const utils = trpc.useUtils();
  const invitesQuery = trpc.team.invite.list.useQuery({ status: "PENDING" });
  const revoke = trpc.team.invite.revoke.useMutation({
    onSuccess: () => {
      utils.team.invite.list.invalidate();
      toast.success("Convite revogado.");
    },
    onError: (err) => toast.error(err.message),
  });
  const resend = trpc.team.invite.resend.useMutation({
    onSuccess: () => {
      utils.team.invite.list.invalidate();
      toast.success("Convite reenviado.");
    },
    onError: (err) => toast.error(err.message),
  });

  const invites = invitesQuery.data ?? [];
  if (invites.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-500/30 dark:bg-amber-500/5">
      <div className="flex items-center justify-between gap-2 border-b border-amber-200/60 px-3 py-2 dark:border-amber-500/20">
        <p className="text-[12px] font-semibold text-amber-900 dark:text-amber-300">
          {invites.length} convite{invites.length > 1 ? "s" : ""} pendente
          {invites.length > 1 ? "s" : ""}
        </p>
      </div>
      <ul className="divide-y divide-amber-200/40 dark:divide-amber-500/15">
        {invites.map((inv: any) => {
          const url = `${env.landingUrl}/equipe/${inv.token}`;
          return (
            <li
              key={inv.id}
              className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] sm:flex-nowrap"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary">
                <Mail className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate font-semibold text-zinc-900 dark:text-foreground">
                    {inv.email}
                  </span>
                  <span className="text-zinc-500 dark:text-muted-foreground">
                    · {ROLE_LABEL[inv.role] ?? inv.role}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  Enviado{" "}
                  {formatDistanceToNow(new Date(inv.createdAt), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                  {inv.expiresAt && (
                    <>
                      {" · expira "}
                      {format(new Date(inv.expiresAt), "dd/MM", { locale: ptBR })}
                    </>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(url);
                    toast.success("Link copiado.");
                  }}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-amber-100 md:h-7 md:w-7 dark:text-muted-foreground dark:hover:bg-amber-500/15"
                  title="Copiar link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => resend.mutate({ id: inv.id })}
                  disabled={resend.isPending}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-brand-700 transition-colors hover:bg-brand-100 md:h-7 md:w-7 dark:text-primary dark:hover:bg-primary/15 disabled:opacity-50"
                  title="Reenviar convite"
                >
                  <SendHorizonal className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Revogar esse convite?")) revoke.mutate({ id: inv.id });
                  }}
                  disabled={revoke.isPending}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-100 md:h-7 md:w-7 dark:hover:bg-red-500/15 disabled:opacity-50"
                  title="Revogar"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
